import { useState } from 'react';
import { RÉGLAGES_GHOST, modeEnPlace, réglagesDuMode } from '@vigie/shared';
import type { GroupeRéglagesGhost, RéglageGhost } from '@vigie/shared';
import type { SourceConfig } from '../api.js';

/**
 * Réglages nécessaires à une installation auto-hébergée, présents ou non dans le fichier.
 *
 * Le formulaire ordinaire ne peut montrer que les clés déjà écrites. Une installation neuve n'en a
 * presque aucune : ni infolettres, ni statistiques, ni réglages de sécurité. Il fallait donc les
 * proposer d'avance — c'est exactement la mise en place qu'on venait faire, et l'alternative était
 * d'aller éditer le fichier en SSH.
 *
 * Les valeurs déjà définies sont affichées telles que le serveur les envoie, secrets masqués
 * compris : le masque est réinjecté côté serveur, donc laisser un secret masqué ne l'écrase pas.
 */

interface Props {
  source: SourceConfig;
  éditions: Record<string, string>;
  setÉditions: (mise: (précédentes: Record<string, string>) => Record<string, string>) => void;
  révélées: Record<string, string>;
  onRévéler: (chemin: string) => void;
  peutModifier: boolean;
  enCours: boolean;
  /** Avertissements du dernier aperçu, rangés par groupe. */
  avertissements: Readonly<Record<string, string[]>>;
}

export function RéglagesRecommandés(props: Props) {
  const { source, éditions, setÉditions, révélées, onRévéler, peutModifier } = props;

  const valeurs = source.keys;
  /**
   * Mode déjà en place, déduit de la configuration.
   *
   * Faire rechoisir à chaque ouverture ce qui est déjà configuré donnerait un site parfaitement
   * réglé qui a l'air en attente d'une décision. La déduction vit dans le catalogue partagé : c'est
   * de la connaissance sur Ghost, et elle s'y teste.
   */
  const [modes, setModes] = useState<Record<string, string>>(() => {
    const àPlat = Object.fromEntries(
      Object.entries(valeurs).map(([clé, entrée]) => [clé, entrée.value]),
    );
    return Object.fromEntries(
      RÉGLAGES_GHOST.filter((groupe) => groupe.modes !== undefined).map((groupe) => [
        groupe.clé,
        modeEnPlace(groupe, àPlat) ?? '',
      ]),
    );
  });

  /** Retenir un mode pose ses valeurs mécaniques : on ne fait pas cocher une case évidente. */
  function choisirMode(groupe: GroupeRéglagesGhost, clé: string) {
    setModes((précédents) => ({ ...précédents, [groupe.clé]: clé }));
    const mode = groupe.modes?.find((candidat) => candidat.clé === clé);
    if (mode?.valeurs === undefined) return;
    setÉditions((précédentes) => ({ ...précédentes, ...mode.valeurs }));
  }

  function champ(réglage: RéglageGhost) {
    const existante = valeurs[réglage.chemin];
    const définie = existante !== undefined;
    const masquéeEncore =
      existante?.masked === true &&
      révélées[réglage.chemin] === undefined &&
      éditions[réglage.chemin] === undefined;
    const affichée = éditions[réglage.chemin] ?? révélées[réglage.chemin] ?? existante?.value ?? '';
    const id = `réglage-${réglage.chemin}`;

    const modifier = (valeur: string) =>
      setÉditions((précédentes) => ({ ...précédentes, [réglage.chemin]: valeur }));

    return (
      <div key={réglage.chemin} className="config-champ">
        <label htmlFor={id}>
          <span className="config-chemin">{réglage.libellé}</span>
          {réglage.requis === true && <span className="etiquette">nécessaire</span>}
          {!définie && <span className="etiquette etiquette--douce">non défini</span>}
        </label>
        <p className="aide">{réglage.aide}</p>
        <div className="config-saisie">
          {réglage.type === 'choix' ? (
            <select
              id={id}
              value={affichée}
              disabled={!peutModifier}
              onChange={(événement) => modifier(événement.target.value)}
            >
              <option value="">— non défini —</option>
              {réglage.choix?.map((choix) => (
                <option key={choix} value={choix}>
                  {choix}
                </option>
              ))}
            </select>
          ) : réglage.type === 'booleen' ? (
            <select
              id={id}
              value={affichée === '' ? '' : String(affichée) === 'true' ? 'true' : 'false'}
              disabled={!peutModifier}
              onChange={(événement) => modifier(événement.target.value)}
            >
              <option value="">— non défini —</option>
              <option value="true">activé</option>
              <option value="false">désactivé</option>
            </select>
          ) : (
            <input
              id={id}
              type="text"
              inputMode={réglage.type === 'nombre' ? 'numeric' : undefined}
              value={affichée}
              placeholder={réglage.exemple ?? ''}
              readOnly={!peutModifier}
              onChange={(événement) => modifier(événement.target.value)}
            />
          )}
          {masquéeEncore && peutModifier && (
            <button
              type="button"
              className="lien"
              disabled={props.enCours}
              onClick={() => onRévéler(réglage.chemin)}
            >
              Révéler
            </button>
          )}
        </div>
        <p className="aide">
          <code>{réglage.chemin}</code>
        </p>
      </div>
    );
  }

  return (
    <div className="config-groupes">
      <p className="aide">
        Ces clés sont proposées même quand le fichier ne les contient pas encore : c’est ainsi qu’on
        met en place les infolettres ou les statistiques d’un site neuf. Les noms de clés ont été
        relevés dans le code de Ghost 6 — une clé mal nommée est ignorée en silence, et l’on croit
        avoir configuré quelque chose.
      </p>

      {RÉGLAGES_GHOST.map((groupe) => {
        const mode = modes[groupe.clé] ?? '';
        const applicables = réglagesDuMode(groupe, mode === '' ? null : mode);
        const alertes = props.avertissements[groupe.clé] ?? [];

        return (
          <fieldset key={groupe.clé} className="config-groupe">
            <legend>{groupe.titre}</legend>
            <p className="aide">{groupe.résumé}</p>
            {groupe.condition !== undefined && (
              <p className="aide">
                <strong>Condition posée par Ghost :</strong> {groupe.condition}
              </p>
            )}
            {alertes.map((message) => (
              <p key={message} className="avertissement">
                {message}
              </p>
            ))}

            {groupe.modes !== undefined && (
              <>
                <div className="bascule-mode">
                  {groupe.modes.map((candidat) => (
                    <button
                      key={candidat.clé}
                      type="button"
                      className={mode === candidat.clé ? 'onglet onglet--actif' : 'onglet'}
                      disabled={!peutModifier}
                      onClick={() => choisirMode(groupe, candidat.clé)}
                    >
                      {candidat.titre}
                    </button>
                  ))}
                </div>
                <p className="aide">
                  {groupe.modes.find((candidat) => candidat.clé === mode)?.résumé ??
                    'Choisis une voie pour voir les clés à renseigner.'}
                </p>
              </>
            )}

            {applicables.map((réglage) => champ(réglage))}
          </fieldset>
        );
      })}
    </div>
  );
}
